import React, { useMemo } from "react";

import { EventGate } from "~/components/event-gate";
import { Slot, type Slottable } from "~/components/slot";
import { cx } from "~/tools/class-variance";
import { useScoped } from "~/tools/impulse";
import type { ImpulseForm } from "~/tools/impulse-form";
import { isPresent } from "~/tools/is-present";

import styles from "./_form.module.css";
import { useFormFieldContext } from "./use-form-field-context";

export interface FormFieldResetProps extends Slottable, React.ComponentPropsWithoutRef<"button"> {
	/**
	 * The ImpulseForm state to reset.
	 * If not provided, it will use the state from the FormField context.
	 */
	state?: ImpulseForm | ReadonlyArray<ImpulseForm | undefined>;
}

export const FormFieldReset = React.forwardRef<HTMLButtonElement, FormFieldResetProps>(
	({ asChild, className, onClick, state, children, ...props }, ref) => {
		const Root = asChild ? Slot : "button";
		const context = useFormFieldContext();
		const formState = state ?? context?.state;

		if (formState == null) {
			throw new Error("FormFieldReset must be used within a FormField component or with a `state` prop.");
		}

		const filteredFormState = useMemo(() => [formState].flat().filter(isPresent), [formState]);

		const dirty = useScoped((scope) => filteredFormState.some((item) => item.isDirty(scope)), [filteredFormState]);

		const submitting = useScoped(
			(scope) => filteredFormState.some((item) => item.isSubmitting(scope)),
			[filteredFormState],
		);

		return (
			// TODO OBSFE-128 utilize the ~/components/link component
			<EventGate<"button">
				aria-busy={submitting}
				aria-hidden={!dirty}
				className={cx(styles.formFieldReset, asChild && styles.unstyled, className)}
				listening={!submitting}
				onClick={(event) => {
					filteredFormState.forEach((item) => {
						item.reset();
					});
					onClick?.(event);
				}}
				ref={ref}
				tabIndex={dirty ? undefined : -1}
				{...props}
			>
				<Root type="button">{children ?? "Reset"}</Root>
			</EventGate>
		);
	},
);
